import "./queryBuilder.scss";
import {
  useState,
  ChangeEvent,
  SyntheticEvent,
  useEffect,
  useMemo,
} from "react";
import {
  Autocomplete,
  Box,
  Typography,
  Button,
  Chip,
  Divider,
  Stack,
  Tabs,
  Tab,
  TextField,
  Grid,
  Backdrop,
  CircularProgress,
  Select,
  MenuItem,
} from "@mui/material";
import { Close, Edit, Folder, MenuBook, Save } from "@mui/icons-material";
import { QueryStatus, useExplorerStore } from "../../../data/ExplorerStore";
import {
  EditQueryForm,
  SavedQuery,
  SavedQueryForm,
} from "../../../models/Explorer";
import { SimpleModal } from "../../Elements/SimpleModal/SimpleModal";
import TabContent from "../../Elements/TabContent";
import QueryForm from "./QueryForm";
import MetricGroupForm from "./MetricGroupForm";
import { RichTreeViewPro } from "@mui/x-tree-view-pro";
import GlossaryContent from "../Glossary/GlossaryContent";
import SavedQueryTreeItem, {
  SavedQueryTreeItemType,
} from "./SavedQueryTreeItem";
import { StyledTextField } from "../../Elements/StyledTextField/StyledTextField";
import { StyledTextArea } from "../../Elements/StyledTextArea/StyledTextArea";
import { StyledRadioButtonsGroup } from "../../Elements/StyledRadioButtons/StyledRadioButtonsGroup";
import { StyledRadioButton } from "../../Elements/StyledRadioButtons/StyledRadioButton";
import { PinnedSourceChip } from "../../Elements/PinnedSource/PinnedSource";

// Deterministic color for a tag based on its string hash
const TAG_COLORS = [
  "#e3f2fd", "#fce4ec", "#e8f5e9", "#fff3e0", "#f3e5f5",
  "#e0f7fa", "#fff8e1", "#ede7f6", "#e8eaf6", "#fbe9e7",
];
export function getTagColor(tag: string): string {
  let hash = 0;
  for (let i = 0; i < tag.length; i++) {
    hash = ((hash << 5) - hash + tag.charCodeAt(i)) | 0;
  }
  return TAG_COLORS[Math.abs(hash) % TAG_COLORS.length];
}

/** Parse a JSON tags string and render as small colored chips. */
export function renderTagChips(tagsJson?: string | null, height = 16) {
  try {
    const tags = tagsJson ? JSON.parse(tagsJson) : null;
    if (!Array.isArray(tags) || tags.length === 0) return null;
    return tags.map((tag: string) => (
      <Chip
        key={tag}
        label={tag}
        size="small"
        sx={{
          height,
          fontSize: height <= 16 ? "0.6rem" : "0.7rem",
          borderRadius: "3px",
          backgroundColor: getTagColor(tag),
          "& .MuiChip-label": { px: 0.5 },
        }}
      />
    ));
  } catch {
    return null;
  }
}

export default function QueryBuilder() {
  const [openDialog, setOpenDialog] = useState<
    "queryBuilder" | "savedQueries" | "saveQuery" | "editQuery" | "glossary" | null
  >(null);
  const [currentTab, setCurrentTab] = useState<string>("query");
  const savedQuery = useExplorerStore(({ savedQuery }) => savedQuery);
  const loadSavedQuery = useExplorerStore(
    ({ loadSavedQuery }) => loadSavedQuery,
  );
  const metricGroupsLength = useExplorerStore(
    ({ metricGroups }) => metricGroups.length,
  );
  // A primitive selector (names joined) keeps the reference stable between renders.
  const metricGroupNames = useExplorerStore(({ metricGroups }) =>
    metricGroups.map((mg) => mg.name ?? "").join("\u0000"),
  ).split("\u0000");
  const pseudoMetricGroupArray = Array.from(
    { length: metricGroupsLength },
    (x, i) => i,
  );
  const addMetricGroup = useExplorerStore(({ addMetricGroup }) => () => {
    addMetricGroup();
    setCurrentTab(`metricGroup${metricGroupsLength}`);
  });
  const removeMetricGroup = useExplorerStore(
    ({ removeMetricGroup }) =>
      (index: number) => {
        removeMetricGroup(index);
        setCurrentTab("query");
      },
  );
  const canRemoveMetricGroup = metricGroupsLength > 2;
  const [formState, setFormState] = useState<SavedQueryForm>({
    overwrite: "new",
    queryName: "",
    description: "",
    saveType: "public",
    tags: [],
  });
  const [overwriteQueryId, setOverwriteQueryId] = useState<string | null>(null);
  const [editQueryForm, setEditQueryForm] = useState<EditQueryForm>(
    {} as EditQueryForm,
  );
  const openEditDialog = (
    queryForm: SavedQueryForm,
    editQueryForm: EditQueryForm,
  ) => {
    setFormState(queryForm);
    setEditQueryForm(editQueryForm);
    setOpenDialog("editQuery");
  };
  const [overwriteError, setOverwriteError] = useState<string | null>(null);
  const [savedQueries, savedQueriesStatus] = useExplorerStore(
    ({ savedQueries, savedQueriesStatus }) => [
      savedQueries,
      savedQueriesStatus,
    ],
  );
  const savedQueriesLoading = savedQueriesStatus === QueryStatus.Requesting;
  const [querySearch, setQuerySearch] = useState("");

  // Collect all unique tags across saved queries for autocomplete suggestions
  const allTags = useMemo(() => {
    const tagSet = new Set<string>();
    savedQueries.forEach((group) =>
      group.forEach((sq) => {
        if (sq.TAGS) {
          try {
            const parsed = JSON.parse(sq.TAGS);
            if (Array.isArray(parsed)) parsed.forEach((t: string) => tagSet.add(t));
          } catch {}
        }
      }),
    );
    return Array.from(tagSet).sort();
  }, [savedQueries]);

  // Score a query against search terms (higher = more relevant)
  const searchScore = (sq: SavedQuery, search: string): number => {
    const terms = search.toLowerCase().split(/\s+/).filter(Boolean);
    let score = 0;
    const name = sq.NAME.toLowerCase();
    const desc = (sq.DESCRIPTION || "").toLowerCase();
    let parsedTags: string[] = [];
    try {
      const t = sq.TAGS ? JSON.parse(sq.TAGS) : [];
      if (Array.isArray(t)) parsedTags = t.map((s: string) => s.toLowerCase());
    } catch {}

    for (const term of terms) {
      if (name.includes(term)) score += 10; // Name match is strongest
      if (parsedTags.some((t) => t.includes(term))) score += 5; // Tag match
      if (desc.includes(term)) score += 2; // Description match
    }
    return score;
  };

  // Top search results across all users (flat list, capped at 5)
  const searchResults = useMemo(() => {
    if (!querySearch.trim()) return [];
    const scored: Array<{ sq: SavedQuery; score: number }> = [];
    savedQueries.forEach((group) =>
      group.forEach((sq) => {
        const s = searchScore(sq, querySearch);
        if (s > 0) scored.push({ sq, score: s });
      }),
    );
    scored.sort((a, b) => b.score - a.score);
    return scored.slice(0, 5).map((s) => s.sq);
  }, [savedQueries, querySearch]);

  const savedQueriesTree: SavedQueryTreeItemType[] = savedQueries.map(
    (sqg) => ({
      id: sqg[0].USER_ID,
      userId: sqg[0].USER_ID || "no owner",
      itemId: sqg[0].USER_ID || "no owner",
      label: sqg[0].USER_NAME,
      type: "folder",
      savedQuery: null,
      orphan: sqg[0].USER_ID === "no owner",
      children: sqg.map((sq) => ({
        id: sq.ID,
        userId: sq.USER_ID,
        itemId: sq.ID,
        label: sq.NAME,
        saveType: sq.SAVE_TYPE as "public" | "private" | "archived",
        type: "query",
        savedQuery: sq,
        orphan: sq.USER_ID === "no owner",
        handleClose: () => setOpenDialog(null),
        openEditDialog,
      })),
    }),
  );
  // Filter tree by search term (fuzzy matches name, description, or tags)
  const filteredTree = useMemo(() => {
    if (!querySearch.trim()) return savedQueriesTree;
    return savedQueriesTree
      .map((folder) => {
        const matchingChildren = (folder.children ?? []).filter((child) => {
          const sq = child.savedQuery;
          if (!sq) return false;
          return searchScore(sq, querySearch) > 0;
        });
        if (matchingChildren.length === 0) return null;
        return { ...folder, children: matchingChildren };
      })
      .filter(Boolean) as SavedQueryTreeItemType[];
  }, [savedQueriesTree, querySearch]);

  const userQueries = useMemo(() => {
    const res = [] as SavedQuery[];
    savedQueries.forEach((sqg) => {
      sqg.forEach((sq) => {
        if (sq.IS_OWNER) {
          res.push(sq);
        }
      });
    });
    return res;
  }, [savedQueries]);
  const resetFormState = (overwrite: "new" | "overwrite") =>
    setFormState({
      overwrite: overwrite,
      queryName: "",
      description: "",
      saveType: "public",
      tags: [],
    });
  const handleSaveQueryClose = () => {
    setOpenDialog(null);
    resetFormState("new");
    setEditQueryForm({} as EditQueryForm);
    setOverwriteError(null);
    setOverwriteQueryId(null);
  };
  const saveQuery = useExplorerStore(({ saveQuery }) => async () => {
    if (formState.overwrite === "overwrite" && overwriteQueryId) {
      handleOverwriteQuery(overwriteQueryId);
    } else {
      await saveQuery(formState);
      handleSaveQueryClose();
    }
  });
  const execQuery = useExplorerStore(({ execQuery }) => () => {
    setOpenDialog(null);
    execQuery("query_builder");
  });
  const handleUpdate = useExplorerStore(({ editQuery }) => () => {
    editQuery(editQueryForm.editQueryId, formState);
    handleSaveQueryClose();
    setOpenDialog("savedQueries");
  });
  const handleOverwriteQuery = useExplorerStore(
    ({ archiveQuery, saveQuery, editQuery }) =>
      async (id: string) => {
        // If the current query hash matches the target, the query content
        // hasn't changed — just update metadata (name/desc/tags/saveType).
        // If hashes differ, the query content changed — save new + archive old.
        const currentHash = savedQuery?.ID;
        if (currentHash && currentHash === id) {
          editQuery(id, formState);
          handleSaveQueryClose();
        } else {
          saveQuery(formState)
            .then(() => {
              archiveQuery(id);
              handleSaveQueryClose();
            })
            .catch((err) => {
              setOverwriteError(`Failed to overwrite query: ${err}`);
            });
        }
      },
  );
  const handleSelectOverwriteQuery = (id: string) => {
    if (id === "none") {
      setOverwriteQueryId(null);
      resetFormState("overwrite");
      return;
    }
    setOverwriteQueryId(id);
    const query = userQueries.find((sq) => sq.ID === id);
    if (query) {
      setFormState({
        queryName: query.NAME,
        description: query.DESCRIPTION,
        saveType: query.SAVE_TYPE as "public" | "private" | "archived",
        overwrite: "overwrite",
      });
    } else {
      setOverwriteError("Error selecting query to overwrite");
    }
  };

  const queryStatus = useExplorerStore(({ queryStatus }) => queryStatus);
  useEffect(() => {
    const url = new URL(window.location.href);
    // Remove legacy #hash if present (migrated to ?q= param)
    if (url.hash) url.hash = "";
    if (savedQuery?.ID) {
      url.searchParams.set("q", savedQuery.ID);
    } else if (queryStatus === QueryStatus.Complete || queryStatus === QueryStatus.Failed) {
      // Only clear ?q= after the system has settled — don't touch it during
      // init (None) or while a query is in flight (Requesting).
      url.searchParams.delete("q");
    }
    window.history.replaceState(null, "", url.toString());
  }, [savedQuery, queryStatus]);

  const queryBuilderActions = (
    <Button variant="outlined" onClick={execQuery}>
      Run
    </Button>
  );

  const saveEditActions =
    openDialog === "editQuery" ? (
      <>
        <Button
          variant="outlined"
          color="secondary"
          disabled={
            formState.queryName === editQueryForm.queryName &&
            formState.description === editQueryForm.description &&
            formState.saveType === editQueryForm.saveType
          }
          onClick={handleUpdate}
        >
          Save
        </Button>
      </>
    ) : (
      <>
        {overwriteError && (
          <Typography color="error">{overwriteError}</Typography>
        )}
        <Button
          variant="outlined"
          disabled={
            !formState.queryName ||
            (formState.overwrite === "overwrite" && !overwriteQueryId)
          }
          onClick={saveQuery}
          color="secondary"
        >
          {savedQueriesLoading ? (
            <CircularProgress size={20} sx={{ padding: 0.5 }} color="inherit" />
          ) : formState.overwrite === "overwrite" ? (
            savedQuery?.ID && savedQuery.ID === overwriteQueryId ? "Update" : "Overwrite"
          ) : (
            "Save"
          )}
        </Button>
      </>
    );

  const buttonItems = [
    {
      label: "Edit",
      icon: <Edit fontSize="small" />,
      onClick: () => setOpenDialog("queryBuilder" as const),
      loading: false,
    },
    {
      label: "Save",
      icon: <Save fontSize="small" />,
      onClick: () => {
        // Check if the current query hash already exists in user's saved queries
        const currentHash = savedQuery?.ID;
        const existingQuery = currentHash
          ? userQueries.find((sq) => sq.ID === currentHash)
          : undefined;
        if (existingQuery) {
          // Pre-select the existing query for overwrite
          let parsedTags: string[] = [];
          try {
            const t = existingQuery.TAGS ? JSON.parse(existingQuery.TAGS) : [];
            if (Array.isArray(t)) parsedTags = t;
          } catch {}
          setFormState({
            overwrite: "overwrite",
            queryName: existingQuery.NAME,
            description: existingQuery.DESCRIPTION,
            saveType: existingQuery.SAVE_TYPE as "public" | "private" | "archived",
            tags: parsedTags,
          });
          setOverwriteQueryId(existingQuery.ID);
        } else {
          resetFormState("new");
        }
        setOpenDialog("saveQuery" as const);
      },
      loading: savedQueriesLoading,
    },
    {
      label: "Queries",
      icon: <Folder fontSize="small" />,
      onClick: () => setOpenDialog("savedQueries" as const),
      loading: savedQueriesLoading,
    },
    // Sits beside the other query-building tools rather than in the top nav:
    // it is only useful while composing a query, and that is where the question
    // "what does this filter actually mean" gets asked.
    {
      label: "Glossary",
      icon: <MenuBook fontSize="small" />,
      onClick: () => setOpenDialog("glossary" as const),
      loading: false,
    },
  ];

  return (
    <>
      {buttonItems.map((item) => (
        <Button
          key={item.label}
          color="secondary"
          variant="outlined"
          onClick={item.onClick}
          disabled={item.loading}
          size="small"
          startIcon={
            item.loading ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              item.icon
            )
          }
          sx={{ py: 0.25, px: 1, textTransform: "none", fontSize: "0.75rem" }}
        >
          {item.label}
        </Button>
      ))}

      {/* Glossary — same content as the /glossary route, in the builder's dialog */}
      <SimpleModal
        isOpen={openDialog === "glossary"}
        handleClose={() => setOpenDialog(null)}
        title="Glossary"
        dialogClassName="query-builder--dialog glossary--dialog"
      >
        <GlossaryContent compact />
      </SimpleModal>

      {/* Query Builder modal */}
      <SimpleModal
        isOpen={openDialog === "queryBuilder"}
        handleClose={() => setOpenDialog(null)}
        actions={queryBuilderActions}
        title="Query Builder"
        dialogClassName="query-builder--dialog"
      >
        <>
          <Tabs
            value={currentTab}
            onChange={(e: SyntheticEvent, newValue: string) =>
              setCurrentTab(newValue)
            }
            variant="scrollable"
            scrollButtons="auto"
            className="query-builder--tabs-container"
          >
            <Tab label="Query" value="query" />
            {pseudoMetricGroupArray.map(
              (x, i) =>
                i !== 0 && (
                  <Tab
                    label={
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        {metricGroupNames[i] || `Metric Group${i > 1 ? ` ${i}` : ""}`}
                        {canRemoveMetricGroup && (
                          <Close
                            sx={{
                              fontSize: "0.85rem",
                              opacity: 0.6,
                              "&:hover": { opacity: 1 },
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              removeMetricGroup(i);
                            }}
                          />
                        )}
                      </Box>
                    }
                    value={`metricGroup${i}`}
                    key={`metricGroup${i}--tab`}
                  />
                ),
            )}
            <Tab label="+ Metric Group" onClick={addMetricGroup} />
          </Tabs>
          <Box className="query-builder--form-area">
            <TabContent tabName="query" currentTab={currentTab}>
              <QueryForm metricGroupIndex={0} />
            </TabContent>
            {pseudoMetricGroupArray.map(
              (x, i) =>
                i !== 0 && (
                  <TabContent
                    tabName={`metricGroup${i}`}
                    currentTab={currentTab}
                    key={`metricGroup${i}--panel`}
                  >
                    <MetricGroupForm i={i} />
                  </TabContent>
                ),
            )}
          </Box>
        </>
      </SimpleModal>

      {/* Saved Queries modal */}
      <SimpleModal
        isOpen={openDialog === "savedQueries"}
        handleClose={() => setOpenDialog(null)}
        title="Saved queries"
        dialogClassName="query-builder--dialog"
      >
        {savedQueriesLoading && (
          <Backdrop
            open={true}
            sx={{
              position: "absolute",
              zIndex: 2000,
              backgroundColor: "rgba(0,0,0,0.25)",
            }}
          >
            <CircularProgress color="inherit" />
          </Backdrop>
        )}
        <Box sx={{ px: 2, pt: 1.5, pb: 1.5 }}>
          <TextField
            size="small"
            fullWidth
            placeholder="Search by name, description, or tag..."
            value={querySearch}
            onChange={(e) => setQuerySearch(e.target.value)}
            sx={{ "& .MuiInputBase-root": { fontSize: "0.85rem" } }}
          />
        </Box>
        {searchResults.length > 0 && (
          <Box sx={{ px: 2, pb: 1 }}>
            <Typography
              fontSize="0.7rem"
              textTransform="uppercase"
              fontWeight={600}
              color="rgba(0,0,0,0.6)"
              mb={0.5}
            >
              Top Results
            </Typography>
            {searchResults.map((sq) => (
              <Box
                key={sq.ID}
                onClick={() => {
                  loadSavedQuery(sq.ID);
                  setOpenDialog(null);
                }}
                sx={{
                  py: 0.75,
                  px: 1,
                  borderRadius: 1,
                  cursor: "pointer",
                  "&:hover": { backgroundColor: "grey.100" },
                }}
              >
                <Typography sx={{ fontSize: "0.85rem", lineHeight: 1.3 }}>
                  {sq.NAME}
                </Typography>
                <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" mt={0.25}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.7rem" }}>
                    {sq.USER_NAME}
                  </Typography>
                  <PinnedSourceChip source={sq.pinnedSource} />
                  {renderTagChips(sq.TAGS)}
                </Stack>
              </Box>
            ))}
            <Divider sx={{ mt: 1 }} />
          </Box>
        )}
        <Grid
          container
          direction="column"
          wrap="wrap"
          sx={{ flex: 1, minHeight: 0, overflow: "auto" }}
        >
          <RichTreeViewPro
            items={filteredTree}
            slots={{ item: SavedQueryTreeItem }}
            getItemId={(item) =>
              item.type === "folder"
                ? item.id
                : `${item.userId}:${item.id}:${item.saveType}`
            }
            disableSelection
            defaultExpandedItems={querySearch.trim() ? filteredTree.map((f) => f.id) : []}
          />
        </Grid>
      </SimpleModal>

      {/* Save query modal */}
      <SimpleModal
        isOpen={openDialog === "saveQuery" || openDialog === "editQuery"}
        handleClose={() => handleSaveQueryClose()}
        title={
          openDialog === "editQuery"
            ? `Edit Query: ${editQueryForm.queryName}`
            : savedQuery?.ID && userQueries.some((sq) => sq.ID === savedQuery.ID)
              ? "Update query"
              : "Save query"
        }
        actions={saveEditActions}
        dialogClassName="query-builder--dialog"
      >
        <Box py={2}>
          {openDialog === "saveQuery" && userQueries.length > 0 && (() => {
            // Detect if current query already exists in user's library
            const isDuplicate = !!savedQuery?.ID && userQueries.some((sq) => sq.ID === savedQuery.ID);
            return (
              <Box mb={2}>
                {isDuplicate && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                    This query already exists in your library. Update its details below.
                  </Typography>
                )}
                {!isDuplicate && (
                  <>
                    <StyledRadioButtonsGroup
                      id="overwrite_query"
                      value={formState.overwrite}
                      row={true}
                      onChange={(e: ChangeEvent<HTMLInputElement>) => {
                        if (e.target.value === "new") {
                          setOverwriteQueryId(null);
                          resetFormState("new");
                        } else {
                          setFormState({
                            ...formState,
                            overwrite: e.target.value as "new" | "overwrite",
                          });
                        }
                      }}
                    >
                      <StyledRadioButton
                        label="Save as new query"
                        value="new"
                        textSize="sm"
                        selected={formState.overwrite === "new"}
                      />
                      <StyledRadioButton
                        label="Overwrite existing query"
                        value="overwrite"
                        textSize="sm"
                        selected={formState.overwrite === "overwrite"}
                      />
                    </StyledRadioButtonsGroup>
                    {formState.overwrite === "overwrite" && (
                      <Select
                        value={overwriteQueryId ?? "none"}
                        sx={{ minWidth: "200px", mt: 2 }}
                        onChange={(e) => handleSelectOverwriteQuery(e.target.value)}
                      >
                        <MenuItem value="none">Select query</MenuItem>
                        {userQueries.map((sq) => (
                          <MenuItem value={sq.ID}>{sq.NAME}</MenuItem>
                        ))}
                      </Select>
                    )}
                  </>
                )}
              </Box>
            );
          })()}
          <StyledTextField
            placeholder={"What would you like to name this query?"}
            label="Query Name"
            value={formState.queryName}
            onChange={(e) => {
              setFormState({ ...formState, queryName: e.target.value });
            }}
          />
          <StyledTextArea
            placeholder={"Write a short description of what this query does"}
            label="Description"
            minRows={3}
            value={formState.description}
            onChange={(e) => {
              setFormState({ ...formState, description: e.target.value });
            }}
          />
          <Autocomplete
            multiple
            freeSolo
            size="small"
            options={allTags}
            value={formState.tags ?? []}
            onChange={(_e, value) =>
              setFormState({ ...formState, tags: value as string[] })
            }
            renderTags={(value, getTagProps) =>
              value.map((tag, index) => (
                <Chip
                  {...getTagProps({ index })}
                  label={tag}
                  size="small"
                  sx={{
                    height: 22,
                    fontSize: "0.75rem",
                    borderRadius: "3px",
                    backgroundColor: getTagColor(tag),
                  }}
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                label="Tags"
                placeholder="Type and press Enter"
                size="small"
              />
            )}
            sx={{ mt: 1 }}
          />
          <StyledRadioButtonsGroup
            id="saveType"
            value={formState.saveType}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              setFormState({
                ...formState,
                saveType: e.target.value as "public" | "private" | "archived",
              });
            }}
            row={true}
          >
            <StyledRadioButton
              label="Public"
              value="public"
              textSize="sm"
              selected={formState.saveType === "public"}
            />
            <StyledRadioButton
              label="Private"
              value="private"
              textSize="sm"
              selected={formState.saveType === "private"}
            />
          </StyledRadioButtonsGroup>
        </Box>
      </SimpleModal>
    </>
  );
}
